// Native IndexedDB wrapper for offline POS operations

const DB_NAME = "GrandHotelPosDb";
const DB_VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("menu")) {
        db.createObjectStore("menu", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("offline_orders")) {
        const orderStore = db.createObjectStore("offline_orders", { keyPath: "offline_id" });
        orderStore.createIndex("synced", "synced", { unique: false });
        orderStore.createIndex("created_at", "created_at", { unique: false });
      }
      if (!db.objectStoreNames.contains("settings")) {
        db.createObjectStore("settings", { keyPath: "key" });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => {
      console.error("IndexedDB open error:", event.target.error);
      reject(event.target.error);
    };
  });
  return dbPromise;
}

// Menu Cache Helpers
export async function saveMenuCache(items) {
  try {
    const db = await openDb();
    const tx = db.transaction("menu", "readwrite");
    const store = tx.objectStore("menu");
    store.clear();
    for (const item of items) {
      store.put(item);
    }
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch (err) {
    console.error("Failed to save menu cache:", err);
    return false;
  }
}

export async function getMenuCache() {
  try {
    const db = await openDb();
    const tx = db.transaction("menu", "readonly");
    const store = tx.objectStore("menu");
    const request = store.getAll();
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
  } catch (err) {
    console.error("Failed to read menu cache:", err);
    return [];
  }
}

// Offline Order Queue Helpers
export async function saveOfflineOrder(order) {
  try {
    const db = await openDb();
    const tx = db.transaction("offline_orders", "readwrite");
    const store = tx.objectStore("offline_orders");
    store.put(order);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(order);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error("Failed to save offline order:", err);
    throw err;
  }
}

export async function getPendingOfflineOrders() {
  try {
    const db = await openDb();
    const tx = db.transaction("offline_orders", "readonly");
    const store = tx.objectStore("offline_orders");
    const request = store.getAll();
    return new Promise((resolve) => {
      request.onsuccess = () => {
        const all = request.result || [];
        resolve(all.filter((o) => !o.synced));
      };
      request.onerror = () => resolve([]);
    });
  } catch (err) {
    console.error("Failed to fetch pending offline orders:", err);
    return [];
  }
}

export async function markOrderSynced(offline_id, server_order) {
  try {
    const db = await openDb();
    const tx = db.transaction("offline_orders", "readwrite");
    const store = tx.objectStore("offline_orders");
    const request = store.get(offline_id);

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const item = request.result;
        if (item) {
          item.synced = true;
          item.synced_at = new Date().toISOString();
          item.server_order_no = server_order?.order_no || null;
          item.server_id = server_order?.id || null;
          store.put(item);
        }
      };
      tx.oncomplete = () => resolve(true);
    });
  } catch (err) {
    console.error("Failed to mark order as synced:", err);
    return false;
  }
}

export async function getOfflineOrderCount() {
  const pending = await getPendingOfflineOrders();
  return pending.length;
}

// Settings Store (Printer Paper Size, Auto Print settings)
export async function savePrinterSettings(settings) {
  try {
    const db = await openDb();
    const tx = db.transaction("settings", "readwrite");
    const store = tx.objectStore("settings");
    store.put({ key: "printer_config", value: settings });
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch (err) {
    console.error("Failed to save printer settings:", err);
    return false;
  }
}

export async function getPrinterSettings() {
  try {
    const db = await openDb();
    const tx = db.transaction("settings", "readonly");
    const store = tx.objectStore("settings");
    const request = store.get("printer_config");
    return new Promise((resolve) => {
      request.onsuccess = () => {
        resolve(request.result?.value || { paperWidth: "80mm", autoPrintBt: false });
      };
      request.onerror = () => resolve({ paperWidth: "80mm", autoPrintBt: false });
    });
  } catch (err) {
    return { paperWidth: "80mm", autoPrintBt: false };
  }
}
